import { LegalClientEntity } from 'api/types/entity';
import { formatToDate, yesOrNotOption } from 'helpers';
import { TFunc } from 'libs/i18n';
import { parseAccountCodesInitialValue } from 'modules/client/client/helpers/getClientInitialValue/parseAccountCodesInitialValue';
import {
  parseAddressValue,
  parseCompanyStateRegistrationValue,
  parseDebtsInitialValue,
  parseRiskLevelInitialValue,
  parseServiceContractInitialValue,
  parseStatusesInitialValue,
  parseW8BENFormValue,
  parseWithdrawalAccountsInitialValue,
} from 'modules/client/common/helpers';

import { LegalClientFormType } from '../../forms/legalClientForm';

import { parseCompanyActivityValue } from './parseCompanyActivityValue';
import { parseCompanyBusinessRrelationshipValue } from './parseCompanyBusinessRrelationshipValue';
import { parseCompanyContactsValue } from './parseCompanyContactsValue';
import { parseCompanyLicense } from './parseCompanyLicense';
import { parseFinanceFinancialMonitoringValue } from './parseFinanceFinancialMonitoringValue';
import { parseFinancingSourcesValue } from './parseFinancingSourcesValue';
import { parseGeneralValue } from './parseGeneralValue';
import { parseGlobalFinancialMonitoringValue } from './parseGlobalFinancialMonitoringValue';
import { parseInvestmentPurposeAndNatureValue } from './parseInvestmentPurposeAndNatureValue';
import { parseKnowledgeAndExperienceValue } from './parseKnowledgeAndExperienceValue';
import { parseLegalAddressValue } from './parseLegalAddressValue';
import { parseLegalClientDocumentsFiles } from './parseLegalClientDocumentsFiles';
import { parseOrderSubmissionContactsInitialValue } from './parseOrderSubmissionContactsInitialValue';
import { parseOrganizationalAndLegalFormValue } from './parseOrganizationalAndLegalFormValue';
import { parseSourcesOfIncomeValue } from './parseSourcesOfIncomeValue';
import { parseTargetedFinancialInstrumentsValue } from './parseTargetedFinancialInstrumentsValue';

export const getLegalClientInitialValue = (
  t: TFunc,
  client: LegalClientEntity
): LegalClientFormType => ({
  reasonChange: { reason: '' },
  companyTaxResidences: client.taxResidences?.map((v) => ({
    country: v.country || null,
    taxNumber: v.taxId,
  })) || [{ country: null, taxNumber: '' }],

  companyRegistrationAddress: parseLegalAddressValue(client.legalAddress),
  companyResidentialAddress: parseAddressValue(client.actualAddress),

  bankAccounts: client.bankAccounts?.map((v) => ({
    country: v.country || null,
    swiftCode: v.swiftCode,
    hasPermanentManagementInCountry:
      yesOrNotOption(t, v.hasPermanentManagementInCountry) ?? null,
    onTerritoryWithNoAML_CFT:
      yesOrNotOption(t, v.onTerritoryWithNoAML_CFT) ?? null,
  })) || [
    {
      country: null,
      swiftCode: '',
      hasPermanentManagementInCountry: null,
      onTerritoryWithNoAML_CFT: null,
    },
  ],

  financingSources: parseFinancingSourcesValue(t, client.financingSource),

  debts: parseDebtsInitialValue(client.debts, t),

  withdrawalAccounts: parseWithdrawalAccountsInitialValue(
    client.withdrawalAccounts
  ),

  orderSubmissionContacts: parseOrderSubmissionContactsInitialValue(
    t,
    client.orderSubmissionContacts
  ),
  OKPOCode: client.OKPOCode ?? undefined,
  activityTypes: client.activityTypes ?? [{ name: '' }],

  businessRelationship: parseCompanyBusinessRrelationshipValue(
    t,
    client.businessRelationship
  ),

  companyContacts: parseCompanyContactsValue(t, client.contacts),

  companyLicense: parseCompanyLicense(t, client),

  companyName: {
    fullName: client.fullName ?? '',
    shortName: client.shortName ?? '',
    fullNameEng: client.fullNameEng ?? '',
    shortNameEng: client.shortNameEng ?? '',
  },

  confirmStructure: { files: client.ownershipStructureFiles },

  constituentDocsFiles: client.constitutionalDocument?.files || [],

  constituentDocsDateOfLastChange:
    client.constitutionalDocument?.dateOfLastChange,

  financeFinancialMonitoring: parseFinanceFinancialMonitoringValue(
    t,
    client.financialMonitoring
  ),

  globalFinancialMonitoring: parseGlobalFinancialMonitoringValue(
    t,
    client.financialMonitoring
  ),

  organizationalAndLegalForm: parseOrganizationalAndLegalFormValue(
    t,
    client.organizationalAndLegalForm
  ),

  registrationPlace: {
    country: client.registrationPlace?.country ?? null,
    locality: client.registrationPlace?.locality ?? '',
    specialRegistrationArea:
      client.registrationPlace?.specialRegistrationArea ?? undefined,
  },

  companyStateRegistration: parseCompanyStateRegistrationValue(
    client.stateRegistration
  ),

  statuses: parseStatusesInitialValue(client, t),

  accountCodes: parseAccountCodesInitialValue(client),

  activity: {
    id: client.id,
    createdAt: formatToDate(client.createdAt),
    brightCode: client.brightCode,
  },

  openAccountOrder_Finance: {
    date: client.openAccountOrder_Finance?.date ?? null,
    number: client.openAccountOrder_Finance?.number ?? '',
    orderReceivedDate:
      client.openAccountOrder_Finance?.orderReceivedDate ?? null,
    document: client.openAccountOrder_Finance?.document
      ? [client.openAccountOrder_Finance.document]
      : undefined,
  },
  additionalDocumentsFiles: client.additionalInformationFiles,
  documentsFiles: parseLegalClientDocumentsFiles(client),

  general: parseGeneralValue(client, t),

  serviceContract: parseServiceContractInitialValue(t, client.serviceContract),

  riskLevel: parseRiskLevelInitialValue(client, t),

  targetedFinancialInstruments: parseTargetedFinancialInstrumentsValue(
    client.targetedFinancialInstruments
  ),
  sourcesOfIncome: parseSourcesOfIncomeValue(client.sourcesOfIncome, t),

  identificationNumbers: {
    LEI: client.identificationNumbers?.LEI,
    GIIN: client.identificationNumbers?.GIIN,
  },
  companyActivity: parseCompanyActivityValue(client.activity, t),

  investmentPurposeAndNature: parseInvestmentPurposeAndNatureValue(
    t,
    client.investmentPurposeAndNature
  ),

  knowledgeAndExperience: parseKnowledgeAndExperienceValue(
    t,
    client.knowledgeAndExperience
  ),

  W8BenForm: parseW8BENFormValue(t, client.W8BENForm),
});
